import { useMemo } from 'react';
import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';

type Props = {
  portfolio: Portfolio;
};

/**
 * Hook that transforms portfolio data into a detailed returns structure.
 *
 * This hook takes a portfolio object and transforms it into a standardized format
 * that includes:
 * - Portfolio-level returns (value, cost basis, gains, etc.)
 * - Individual holding returns with detailed metrics
 *
 * @param portfolio - The portfolio object containing holdings and analysis data
 * @returns Transformed data structure with portfolio and holding-level returns
 */
export const useMockedPortfolioDetailedReturns = ({ portfolio }: Props) => {
  return useMemo(() => {
    const { id, currencyISO, holdings, type, manualInputOption } = portfolio;
    const now = new Date().toISOString();

    const transformedHoldings = holdings.map((holding) => {
      const tradingCurrencyISO = getTradingItemCurrencyISO(
        holding.company?.data?.currencyInfo
      );

      return {
        id: holding.id,
        symbol: holding.symbol,
        company: {
          name: holding.company?.name || '',
          uniqueSymbol: holding.company?.uniqueSymbol || '',
          canonicalURL: holding.company?.canonicalURL || '',
          info: {
            url: holding.company?.info?.url || '',
          },
          data: {
            currencyInfo: {
              trading_item_currency_iso:
                tradingCurrencyISO || holding.currencyISO,
            },
          },
        },
        detailedReturns: {
          startDate: now,
          endDate: now,
          value: holding.analysis?.value || 0,
          costBasis: holding.analysis?.capitalInvested || 0,
          unrealizedGains: holding.analysis?.roiValue || 0,
          unrealizedGainsPercent: holding.analysis?.roi || 0,
          realizedGains: 0,
          realizedGainsPercent: 0,
          dividends: 0,
          dividendsPercent: 0,
          currencyGains: 0,
          currencyGainsPercent: 0,
          totalGains: holding.analysis?.roiValue || 0,
          totalGainsPercent: holding.analysis?.roi || 0,
          annualisedReturn: 0,
          units: holding.units || 0,
          price: holding.company.analysisValue?.lastSharePrice || 0,
        },
        detailedTransactions: [],
      };
    });

    const portfolioReturns = {
      startDate: now,
      endDate: now,
      type,
      manualInputOption,
      value: portfolio.analysis?.value || 0,
      costBasis: portfolio.analysis?.capitalInvested || 0,
      unrealizedGains: portfolio.analysis?.roiValue || 0,
      unrealizedGainsPercent: portfolio.analysis?.roi || 0,
      realizedGains: 0,
      realizedGainsPercent: 0,
      dividends: 0,
      dividendsPercent: 0,
      currencyGains: 0,
      currencyGainsPercent: 0,
      totalGains: portfolio.analysis?.roiValue || 0,
      totalGainsPercent: portfolio.analysis?.roi || 0,
      annualisedReturn: 0,
    };

    return {
      data: {
        Portfolio: {
          id,
          type,
          manualInputOption,
          currencyISO,
          detailedReturns: portfolioReturns,
          holdings: transformedHoldings,
          liquidatedHoldings: [],
        },
      },
    };
  }, [portfolio]);
};
